import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export interface InfiniteListPage<T> {
  rows: T[];
  total: number;
}

type FetchFn<T> = (cursor: string | undefined, limit: number) => Promise<InfiniteListPage<T>>;

export function useInfiniteList<T extends { id: number | string }>(
  queryKey: unknown[],
  fetchFn: FetchFn<T>,
  options: {
    limit?: number;
    getTimestamp: (item: T) => string;
    enabled?: boolean;
    refetchInterval?: number | false;
  },
) {
  const limit = options.limit ?? 50;

  return useInfiniteQuery<InfiniteListPage<T>, Error, { pages: InfiniteListPage<T>[] }, unknown[], string | undefined>({
    queryKey,
    queryFn: ({ pageParam }) => fetchFn(pageParam, limit),
    getNextPageParam: (lastPage) => {
      if (lastPage.rows.length < limit) return undefined;
      const last = lastPage.rows[lastPage.rows.length - 1];
      return last ? options.getTimestamp(last) : undefined;
    },
    initialPageParam: undefined,
    enabled: options.enabled !== false,
    refetchInterval: options.refetchInterval ?? false,
  });
}

// Generic helper: build an API fetcher for list endpoints that accept ?cursor=
export function makeApiFetcher<T extends { id: number | string }>(
  endpoint: string,
  params: Record<string, string | boolean | number | undefined>,
): FetchFn<T> {
  return async (cursor, limit) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== false && v !== '') qs.set(k, String(v));
    }
    qs.set('limit', String(limit));
    if (cursor) qs.set('cursor', cursor);
    return api.get<InfiniteListPage<T>>(`${endpoint}?${qs.toString()}`);
  };
}
